import React, { useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useMakeDispatchCallback } from 'hooks'
import { setSelectedIdWithDrawer } from 'actions/navigator'
import { useSelector, useMakeSelector } from 'hooks/redux'
import { getSelectedId } from 'selectors/navigator'
import { makeGetPinCircuit } from 'selectors/feature/harnesses'

import { classNames } from 'utils/utils'
import { compose } from 'ramda'
import { Paragraph } from 'components/patterns'
import { FeatureWarningIconStyled } from 'components/patterns/Icon/IconsStyled'
import ReadOnlyScaling from 'components/feature/NavigatorPanes/editors/base/ReadOnlyScaling'

import withStyledNode, {
  StyledHeader,
  StyledContent,
  Overlay,
} from 'components/feature/node.style'
import { getIsSimulatedVirtualFuseOpen } from 'selectors/feature/simulation'
import withStyle, {
  StyledContentInner,
  OutputValueRow,
} from './OutputNode.style'
import OutputNodeType from './OutputNodeType'
import { getOutputValues } from './utils'

/* eslint-disable react-hooks/exhaustive-deps */
const OutputNode = ({
  output,
  outputQuery,
  featureModuleType,
  featureModuleName,
  className,
  outputQueryDescription,
  outputQueryUri,
  outputQueryType,
  hasErrors,
  isSimulating,
  scrollTo,
  queryTranslations,
  queryTranslationType,
  featureType,
  showAddOption,
  isActivePowerNetLevel,
}) => {
  const setSelectedIdCallback = useMakeDispatchCallback(setSelectedIdWithDrawer)

  const outputNodeRef = useRef(null)

  const selectedId = useSelector(getSelectedId)

  useEffect(() => {
    if (selectedId === output.id) {
      scrollTo(outputNodeRef)
    }
  }, [selectedId])

  const handleClick = () => {
    setSelectedIdCallback(id)
  }

  const {
    description,
    id,
    current,
    polarity,
    type,
    frequency,
    frequencyMin,
    frequencyMax,
    virtualFuseSetting,
    resetType,
    resetAttempts,
    startBit,
    numberOfBits,
    pwmMode,
    dutyCycle,
    dutyCycleMin,
    dutyCycleMax,
    powerNetLevel,
    isAwake,
    resetDelay,
    scalingQuery,
    mappedOutputId,
    floatingCircuit,
  } = output

  const circuit = useMakeSelector(makeGetPinCircuit, { id: mappedOutputId })
  const simulatedFuseOpen = useSelector(getIsSimulatedVirtualFuseOpen, {
    outputId: id,
  })
  const {
    inputConfig: queryPolarity,
    value: queryValue,
    subType: querySubType,
    startBit: queryStartBit,
    numberOfBits: queryNumberOfBits,
  } = outputQuery

  const isSelected = selectedId === id

  const renderOutputDetails = () => {
    const outputValues = getOutputValues({
      current,
      polarity,
      type,
      frequency,
      frequencyMin,
      frequencyMax,
      virtualFuseSetting,
      resetType,
      resetAttempts,
      startBit,
      numberOfBits,
      pwmMode,
      dutyCycle,
      dutyCycleMin,
      dutyCycleMax,
      isAwake,
      featureModuleType,
      featureModuleName,
      outputQueryDescription,
      outputQueryType,
      powerNetLevel,
      resetDelay,
      querySubType,
      queryPolarity,
      queryValue,
      queryTranslations,
      queryTranslationType,
      queryStartBit,
      queryNumberOfBits,
      circuit,
      featureType,
      mappedOutputId,
      floatingCircuit,
      isActivePowerNetLevel,
      isSimulating,
      simulatedFuseOpen,
    })

    return outputValues.map(({ label, value }) => (
      <OutputValueRow
        key={`${label}-${value}`}
        label={label}
        value={value}
        size="small"
      />
    ))
  }

  return (
    <div
      ref={outputNodeRef}
      className={classNames('function-output-box readonly', className, {
        isSelected,
      })}
      data-testid="output-node"
    >
      <StyledContent className="details">
        <Overlay
          onClick={handleClick}
          data-testid="output-node-click-overlay"
        />
        <StyledHeader className="header">
          <Paragraph weight="bold">{description}</Paragraph>
        </StyledHeader>
        <OutputNodeType
          output={output}
          outputQueryUri={outputQueryUri}
          isSelected={isSelected}
          isSimulating={isSimulating}
          showAddOption={showAddOption}
        />
        <StyledContentInner>
          {renderOutputDetails()}
          {scalingQuery && (
            <ReadOnlyScaling
              uri={scalingQuery}
              withBitTable={false}
              isScalingQuery={true}
            />
          )}
        </StyledContentInner>

        {hasErrors && <FeatureWarningIconStyled />}
      </StyledContent>
    </div>
  )
}

OutputNode.propTypes = {
  className: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  featureModuleType: PropTypes.string.isRequired,
  featureModuleName: PropTypes.string.isRequired,
  output: PropTypes.shape({
    current: PropTypes.number,
    polarity: PropTypes.string,
    /** Differentiates Pin, Native, CAN */
    type: PropTypes.string,
    activeStateRange: PropTypes.array,
    description: PropTypes.string,
    /** Location on the connector */
    location: PropTypes.string,
    connector: PropTypes.string,
    mappedOutputId: PropTypes.string,
    outputDescription: PropTypes.string,
    id: PropTypes.string,
    frequency: PropTypes.number,
    dutyCycle: PropTypes.number,
    virtualFuseSetting: PropTypes.number,
    resetType: PropTypes.string,
    resetAttempts: PropTypes.number,
    fnId: PropTypes.string,
    startBit: PropTypes.number,
    numberOfBits: PropTypes.number,
    powerNetLevel: PropTypes.number,
    powerNetLevels: PropTypes.array,
    isAwake: PropTypes.bool,
    resetDelay: PropTypes.number,
    frequencyMin: PropTypes.number,
    frequencyMax: PropTypes.number,
    pwmMode: PropTypes.number,
    dutyCycleMin: PropTypes.number,
    dutyCycleMax: PropTypes.number,
    scalingQuery: PropTypes.string,
    floatingCircuit: PropTypes.string,
  }).isRequired,
  outputQuery: PropTypes.object,
  selectedId: PropTypes.string,
  isSimulating: PropTypes.bool,
  isMarkedForDeletion: PropTypes.bool,
  outputQueryDescription: PropTypes.string,
  outputQueryUri: PropTypes.string,
  queryTranslations: PropTypes.array,
  queryTranslationType: PropTypes.string,
  outputQueryType: PropTypes.string,
  hasErrors: PropTypes.bool,
  scrollTo: PropTypes.func,
  featureType: PropTypes.string,
  showAddOption: PropTypes.bool,
  isActivePowerNetLevel: PropTypes.bool,
}

export default compose(withStyle, withStyledNode)(OutputNode)
